import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WeddingProvider } from './context/WeddingContext';
import { trackPageView } from './lib/analytics';

import Landing from './pages/Landing';
import Login from './pages/Auth/Login';
import Signup from './pages/Auth/Signup';
import ForgotPassword from './pages/Auth/ForgotPassword';
import { DashboardLayout } from './components/layout/DashboardLayout';

// SPA atrás de hash-bundle: quando fazemos deploy, o hash de cada chunk
// muda. Sessões antigas no navegador (sem reload) tentam buscar URLs que
// não existem mais e quebram com "Failed to fetch dynamically imported
// module". Esse wrapper detecta o erro e força reload da página uma única
// vez (evita loop infinito) pra carregar o bundle atualizado.
//
// CRÍTICO: a flag de "já tentei reload" SÓ pode ser limpa quando um chunk
// efetivamente carregar com sucesso. Limpar no `window.load` causa loop
// infinito quando o reload não resolve (mobile com cache agressivo, problema
// de rede transiente, etc.) — a flag é limpa, o chunk falha de novo, reload
// dispara de novo, ad infinitum.
function lazyWithRetry(importer) {
  return lazy(async () => {
    try {
      const mod = await importer();
      // Sucesso real — limpa a flag pra próximos deploys poderem usar.
      try { sessionStorage.removeItem('mdc:chunk-reload-attempted'); } catch { /* noop */ }
      return mod;
    } catch (err) {
      const msg = String(err?.message || err || '');
      const chunkError = /Failed to fetch dynamically imported module|Loading chunk|Importing a module script failed|ChunkLoadError/i.test(msg);
      if (chunkError) {
        const flag = 'mdc:chunk-reload-attempted';
        if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(flag)) {
          sessionStorage.setItem(flag, String(Date.now()));
          window.location.reload();
          // Mantém a Promise pendente enquanto o reload acontece.
          return new Promise(() => {});
        }
      }
      throw err;
    }
  });
}

// Rotas pesadas: carregadas sob demanda para reduzir o bundle inicial.
const About = lazyWithRetry(() => import('./pages/About'));
const Ebook = lazyWithRetry(() => import('./pages/Ebook'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Terms = lazyWithRetry(() => import('./pages/Legal/Terms'));
const Privacy = lazyWithRetry(() => import('./pages/Legal/Privacy'));
const Onboarding = lazyWithRetry(() => import('./pages/Onboarding'));
const DashboardHome = lazyWithRetry(() => import('./pages/Dashboard/index'));
const Cards = lazyWithRetry(() => import('./pages/Dashboard/Cards'));
const AIGenerator = lazyWithRetry(() => import('./pages/Dashboard/AIGenerator'));
const Rsvps = lazyWithRetry(() => import('./pages/Dashboard/Rsvps'));
const RsvpPrintView = lazyWithRetry(() => import('./pages/Dashboard/RsvpPrintView'));
const Mimos3D = lazyWithRetry(() => import('./pages/Dashboard/Mimos3D'));
const Settings = lazyWithRetry(() => import('./pages/Dashboard/Settings'));
const Support = lazyWithRetry(() => import('./pages/Dashboard/Support'));
const Referrals = lazyWithRetry(() => import('./pages/Dashboard/Referrals'));
const Admin = lazyWithRetry(() => import('./pages/Admin/index'));
const WeddingPage = lazyWithRetry(() => import('./pages/Public/WeddingPage'));
const BlogList = lazyWithRetry(() => import('./pages/Blog/BlogList'));
const BlogPost = lazyWithRetry(() => import('./pages/Blog/BlogPost'));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-rose-50">
      <div className="flex flex-col items-center gap-3">
        <svg className="animate-spin h-8 w-8 text-rose-400" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <p className="text-rose-400 text-sm font-medium">Carregando...</p>
      </div>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <RouteFallback />;
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return user.email === 'admin@mimodocasal.com.br'
    ? children
    : <Navigate to="/dashboard" replace />;
}

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return !user ? children : <Navigate to="/dashboard" replace />;
}

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Em SPA o React Router não reinicia o scroll ao navegar entre rotas — o
  // padrão de browser só vale em load real. Aqui forçamos topo ao mudar de
  // pathname. Quando há hash (#section), respeitamos: o usuário pediu uma
  // âncora específica.
  useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <RouteTracker />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/sobre" element={<About />} />
        <Route path="/guia-casamento" element={<Ebook />} />
        <Route path="/contato" element={<Contact />} />
        <Route path="/termos" element={<Terms />} />
        <Route path="/privacidade" element={<Privacy />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/signup" element={<GuestRoute><Signup /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />

        {/* Protected onboarding */}
        <Route path="/onboarding" element={<PrivateRoute><Onboarding /></PrivateRoute>} />

        {/* Protected dashboard */}
        <Route path="/dashboard" element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
          <Route index element={<DashboardHome />} />
          <Route path="cards" element={<Cards />} />
          <Route path="ai-generator" element={<AIGenerator />} />
          <Route path="rsvps" element={<Rsvps />} />
          <Route path="mimos3d" element={<Mimos3D />} />
          <Route path="settings" element={<Settings />} />
          <Route path="support" element={<Support />} />
          <Route path="indicacoes" element={<Referrals />} />
        </Route>

        {/* View de impressão — fora do DashboardLayout de propósito (sem
            sidebar/topbar), abre numa aba nova pra virar um "documento" limpo
            que o casal imprime/salva como PDF. */}
        <Route path="/dashboard-print/rsvps" element={<PrivateRoute><RsvpPrintView /></PrivateRoute>} />

        {/* Admin */}
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />

        {/* Public wedding pages - must come last */}
        <Route path="/:slug" element={<WeddingPage />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WeddingProvider>
          <AppRoutes />
        </WeddingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
